import { z } from 'zod'

import type { VendorOrderSort } from '@/client'
import type { ListParams } from '@/hooks/queries/vendor-orders'
import {
  idParam,
  isoDayParam,
  limitParam,
  pageParam,
  startOfDay,
  startOfNextDay,
} from '@/lib/listSearch'

/**
 * The view state the vendor-orders page reads from the URL.
 *
 * Same conventions as the purchase-orders page: every field optional with
 * `.catch(undefined)`, defaults applied at read time and stripped on the way out,
 * dates as `yyyy-mm-dd`, and `detail` for the open order so the panel is a link.
 */
const SORTS = [
  '-created',
  'created',
  '-vo_date',
  'vo_date',
  'code',
  '-code',
  '-amount',
  'amount',
] as const satisfies readonly VendorOrderSort[]

export const vendorOrderSearchSchema = z.object({
  page: pageParam,
  limit: limitParam,
  q: z.string().optional().catch(undefined).describe('Text matched against the vendor order code.'),
  sku: z
    .string()
    .optional()
    .catch(undefined)
    .describe('A SKU code a vendor order must have a line for.'),
  warehouse: idParam.describe('The warehouse id the vendor orders arrive at.'),
  vendor: idParam.describe('The vendor id the orders are placed with.'),
  from: isoDayParam.describe('The first day the order was created, YYYY-MM-DD, inclusive.'),
  to: isoDayParam.describe('The last day the order was created, YYYY-MM-DD, inclusive.'),
  sort: z
    .enum(SORTS)
    .optional()
    .catch(undefined)
    .describe('The sort key; a leading - is descending.'),
  detail: idParam.describe('The id of the vendor order open in the side sheet.'),
})

export type VendorOrderSearch = z.infer<typeof vendorOrderSearchSchema>

export const VENDOR_ORDER_SEARCH_DEFAULTS = {
  page: 1,
  limit: 20,
  sort: '-created',
} as const satisfies { page: number; limit: number; sort: VendorOrderSort }

/** Drop any value that equals its default, or is empty, so a URL carries only what differs. */
export function withoutSearchDefaults(search: VendorOrderSearch): VendorOrderSearch {
  const pruned: VendorOrderSearch = {}
  if (search.page !== undefined && search.page !== VENDOR_ORDER_SEARCH_DEFAULTS.page) {
    pruned.page = search.page
  }
  if (search.limit !== undefined && search.limit !== VENDOR_ORDER_SEARCH_DEFAULTS.limit) {
    pruned.limit = search.limit
  }
  if (search.sort !== undefined && search.sort !== VENDOR_ORDER_SEARCH_DEFAULTS.sort) {
    pruned.sort = search.sort
  }
  if (search.q) pruned.q = search.q
  if (search.sku) pruned.sku = search.sku
  if (search.warehouse !== undefined) pruned.warehouse = search.warehouse
  if (search.vendor !== undefined) pruned.vendor = search.vendor
  if (search.from) pruned.from = search.from
  if (search.to) pruned.to = search.to
  if (search.detail !== undefined) pruned.detail = search.detail
  return pruned
}

/** What the list endpoint is asked for, from what the URL says. */
export function toListParams(search: VendorOrderSearch): ListParams {
  return {
    page: search.page ?? VENDOR_ORDER_SEARCH_DEFAULTS.page,
    limit: search.limit ?? VENDOR_ORDER_SEARCH_DEFAULTS.limit,
    sort: search.sort ?? VENDOR_ORDER_SEARCH_DEFAULTS.sort,
    q: search.q || undefined,
    skuCode: search.sku || undefined,
    warehouseId: search.warehouse,
    vendorId: search.vendor,
    createdFrom: search.from ? startOfDay(search.from) : undefined,
    createdTo: search.to ? startOfNextDay(search.to) : undefined,
  }
}
